import React, {useCallback, useState} from 'react';

import debounce from 'lodash.debounce';
import {WidthItem} from 'src/components/width-item';
import {useEffectWithDeps} from 'src/hooks/useEffectWithDeps';
import {getPossibleAccountManagers} from 'src/openapi/admin/apis/GetAccountPossibleManagerApi';
import {useAppDispatch} from 'src/store/hooks';
import {isDeepEqual} from 'src/utils/isDeepEqual';

import {Autosuggest} from '@pb/kit-component-autosuggest-v2';
import {useAbortSignal} from '@pb/kit-hooks-use-abort-signal';

import {AccountTeamMemberSuggestResult} from './account-team-meber-suggest-result';

import type {ChangeEventHandler} from 'react';
import type {AccountManagerSearchResult} from 'src/openapi/admin/models/AccountManagerSearchResult';
import type {ServerResponse} from 'src/typedef';

type Props = {
    id: string,
    selectedTeamMember?: AccountManagerSearchResult | undefined,
    onSelect: (member: AccountManagerSearchResult | undefined) => void,
    selectedTeamMembers?: Array<AccountManagerSearchResult | undefined>,
    color?: 'DEFAULT' | 'SUCCESS' | 'ERROR' | undefined,
    label?: string,
    width?: number,
    placeholder?: string,
};

const MAX_RESULTS = 30;

export const AccountTeamMemberSuggest = (props: Props) => {
    const {id, selectedTeamMember, onSelect, placeholder, selectedTeamMembers, color, width = 430} = props;
    const apiDispatch = useAppDispatch();
    const {createAbortSignal, abort} = useAbortSignal();

    const [query, setQuery] = useState(selectedTeamMember?.email || '');
    const [busy, setBusy] = useState(false);
    const [response, setResponse] = useState<ServerResponse<Array<AccountManagerSearchResult>> | null>(null);

    useEffectWithDeps((prev, current) => {
        if (!isDeepEqual({prev, current})) {
            setQuery(selectedTeamMember?.email || '');
        }
    }, selectedTeamMember);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const getSuggestions = useCallback(debounce(async (newValue: string) => {
        const signal = createAbortSignal();
        const name = newValue.toLowerCase().trim();
        if (name) {
            const searchResult = await apiDispatch(getPossibleAccountManagers({name}, {signal}));
            if (searchResult.payload.success) {
                setResponse({data: searchResult.payload.data.filter((user: AccountManagerSearchResult) => (
                    selectedTeamMembers
                        ? !selectedTeamMembers.some(teamMember => teamMember?.id === user.id)
                        : true
                )).slice(0, MAX_RESULTS)});
            } else {
                setResponse({error: {
                    message: 'Can\'t suggest account team members. Service is temporary unavailable.',
                }});
            }
            setBusy(false);
        } else {
            setBusy(false);
            setResponse(null);
            abort();
        }
    }, 1000), [selectedTeamMembers]);

    const onChangeQuery = useCallback<ChangeEventHandler<HTMLInputElement>>(event => {
        const value = event.currentTarget.value;
        setBusy(true);
        setQuery(value);
        getSuggestions(value);
    }, [getSuggestions]);

    const clearSuggest = useCallback(() => {
        setQuery('');
        onSelect(undefined);
        setResponse(null);
    }, [onSelect]);

    const onItemSelect = useCallback((member: AccountManagerSearchResult) => {
        onSelect(member);
        setQuery(member.email || '');
        setResponse(null);
    }, [onSelect]);

    return (
        <WidthItem width={width}>
            <Autosuggest
                color={color}
                id={id}
                value={query}
                onChange={onChangeQuery}
                placeholder={placeholder}
                onItemSelect={onItemSelect}
                onClear={clearSuggest}>
                <AccountTeamMemberSuggestResult
                    id={`${id}-result`}
                    busy={busy}
                    response={response}
                />
            </Autosuggest>
        </WidthItem>
    );
};
